import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Dispatch, type SetStateAction } from 'react';

import { logger } from '../../../logging/logger';
import {
	MATCH_NO_COOKIE,
	NO_COOKIE_HOST,
	SDK_GLOBAL,
	SDK_GLOBAL_READY,
	SDK_URL,
} from './constants';
import { YouTubePlayer, YouTubePlayerEvent, YouTubeVideoEmbedProps } from './types';
import {
	getSDK,
	handleErrorDefault,
	onStateChange,
	parseEndTime,
	parsePlaylist,
	parseStartTime,
} from './utils';

export interface UseYouTubePlayerLoaderProps
	extends Omit<YouTubeVideoEmbedProps, 'fallbackImg' | 'className'> {
	container: HTMLElement | null;
}

export const useYouTubePlayerLoader = ({
	container,
	autoplay = false,
	loop = false,
	muted = false,
	controls = true,
	playsinline,
	videoId,
	url,
	playerVars,
	onPlay,
	onPause,
	onBuffer,
	onBufferEnd,
	onEnded,
	onReady,
	onUnstarted,
	onError,
	onPlaybackRateChange,
	onPlaybackQualityChange,
}: UseYouTubePlayerLoaderProps): YouTubePlayer | null => {
	const player = useRef<YouTubePlayer | null>(null);
	const [isApiReady, setIsApiReady] = useState(false);
	const [playerLoaded, setPlayerLoaded] = useState(false);

	// IMPORTANT: Sets global API ready state and assigns it a new value
	useEffect(() => {
		if (window[SDK_GLOBAL_READY]) {
			const previousOnApiReady = window[SDK_GLOBAL_READY];

			window[SDK_GLOBAL_READY] = () => {
				previousOnApiReady();
				setIsApiReady(true);
			};
		} else {
			window[SDK_GLOBAL_READY] = () => {
				setIsApiReady(true);
			};
		}
	}, []);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const currentPlayer = useMemo(() => player.current, [player, playerLoaded]);

	const loadPlayer = useCallback(async () => {
		try {
			const YT = await getSDK(SDK_URL, SDK_GLOBAL);

			if (!container || !videoId || !url || !YT) return;

			const youTubePlayer = await new YT.Player(container, {
				width: '100%',
				height: '100%',
				videoId: videoId,
				playerVars: {
					autoplay: autoplay ? 1 : 0,
					mute: autoplay || muted ? 1 : 0,
					controls: controls ? 1 : 0,
					loop: loop ? 1 : 0,
					disablekb: controls ? 0 : 1,
					start: url && parseStartTime(url),
					end: url && parseEndTime(url),
					origin: window.location.origin,
					playsinline: playsinline ? 1 : 0,
					enablejsapi: 1,
					showinfo: 0,
					rel: 0,
					iv_load_policy: 3,
					modestbranding: 1,
					...(url && parsePlaylist(url)),
					...playerVars,
				},
				events: {
					onReady: () => {
						player.current = youTubePlayer;
						setPlayerLoaded(true);

						if (loop) {
							youTubePlayer.setLoop(true); // Enable playlist looping
						}
						onReady?.(youTubePlayer);
					},
					onEnded: () => {
						if (loop) {
							youTubePlayer.playVideo();
						}
					},
					onPlaybackRateChange: (event: YouTubePlayerEvent) => onPlaybackRateChange?.(event),
					onPlaybackQualityChange: (event: YouTubePlayerEvent) => onPlaybackQualityChange?.(event),
					onStateChange: (event: YouTubePlayerEvent) =>
						onStateChange(
							event,
							youTubePlayer,
							{
								onUnstarted,
								onPlay,
								onPause,
								onBuffer,
								onReady,
								onEnded,
								onBufferEnd,
							},
							{ loop, autoplay, start: playerVars?.start },
						),
					onError: (event: YouTubePlayerEvent) => {
						if (onError) {
							onError(event.data);
						} else {
							handleErrorDefault(event.data, url);
						}
					},
				},
				host: !Array.isArray(url) && MATCH_NO_COOKIE.test(url) ? NO_COOKIE_HOST : undefined,
			});
		} catch (error) {
			logger.error('YouTube Player error', { error, videoId });
		}
	}, [
		container,
		autoplay,
		loop,
		muted,
		controls,
		playsinline,
		videoId,
		url,
		playerVars,
		onPlay,
		onPause,
		onBuffer,
		onBufferEnd,
		onEnded,
		onReady,
		onUnstarted,
		onError,
		onPlaybackRateChange,
		onPlaybackQualityChange,
	]);

	useEffect(() => {
		if (!player.current && url && container) {
			loadPlayer();
		}
	}, [player, loadPlayer, container, isApiReady, url]);

	return currentPlayer;
};

export const useYouTubePlayerControls = (
	player: YouTubePlayer | null,
	setShowFallbackImage: Dispatch<SetStateAction<boolean>>,
): {
	handlePlayVideo: () => void;
	handlePauseVideo: () => void;
} => {
	const handlePlayVideo = useCallback(() => {
		if (!player) return;

		setShowFallbackImage(false);
		player?.playVideo();
	}, [player, setShowFallbackImage]);

	const handlePauseVideo = useCallback(() => {
		if (!player) return;

		player?.pauseVideo();
		setShowFallbackImage(true);
	}, [player, setShowFallbackImage]);

	return { handlePlayVideo, handlePauseVideo };
};
